import { useCallback, useLayoutEffect, useMemo } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useAnonymousPathPatterns } from "src/pages/hooks/anonymous-path-patterns";
import { agreementCheckResultState, isJustLoggedInState } from "src/recoil/auth/atom";
import { LogLevel, LoggerState, getDefaultLogLevel, loggerState } from "src/recoil/logger/atom";
import { getAppTitle, getAuthConfig } from "src/utils/app-Info";
import log, { setLogLevel } from "src/utils/log";
import { CourtAuthError, CourtAuthProvider, CourtAuthState } from "../court-auth";
import { resolveReturnUri } from "../court-auth/utils/return-uri";
import Layout from "./Layout";
import MetaTag from "./MetaTag";

/**
 * App > Container
 */
function Container() {
  const navigate = useNavigate();
  // useNaverMap();
  // 인증을 거치지 않는 anonymous route path 를 관리하는 hook
  const { matchAnonymous } = useAnonymousPathPatterns();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const logLevel = params.get("__logLevel");

  const [logger, setLogger] = useRecoilState(loggerState);
  const setIsJustLoggedIn = useSetRecoilState(isJustLoggedInState);
  const setAgreementResult = useSetRecoilState(agreementCheckResultState);

  // 인증(사용자 로그인) 후 리다이렉트 되었을 경우 호출
  const onRedirect = useCallback(
    (state: CourtAuthState) => {
      setIsJustLoggedIn(true); // 로그인 성공 시 상태 설정
      // 신규 로그인 시 약관 체크를 초기화해서 재검증 유도
      setAgreementResult(null);

      const { returnTo } = state;
      log("Container > onRedirect", `returnTo: ${JSON.stringify(returnTo, null, 2)}`);
      // 복귀 경로가 없거나 인증 흐름 경로(로그인/콜백)면 메인으로 보낸다.
      // 인증 흐름 경로로 복귀하면 다시 인가 요청이 일어나 인증 화면으로 되돌아가는 무한 루프가 된다.
      const returnUri = resolveReturnUri(returnTo?.latest?.uri);
      console.debug("returnUri", returnUri);
      navigate(returnUri, { replace: true });
    },
    [navigate, setIsJustLoggedIn, setAgreementResult],
  );

  // 인증 처리(사용자 로그인, 쿠키에서 로드, 토큰 갱신) 중 에러 발생 시 호출
  const onError = useCallback(
    (error: any) => {
      log("Container > onError", error);
      if ("code" in error) {
        const tokenError = error as CourtAuthError;
        switch (tokenError.code) {
          case "jwt::expired":
            log("Container > onError", `code: ${tokenError.code}, message: ${tokenError.message}`);
            break;
        }
      }

      if (!matchAnonymous) {
        log("Container > onError > 인증 페이지인데 오류 발생");

        // 로그인 페이지 이동은 auth-required.tsx 에서 처리함
      }
    },
    [navigate, matchAnonymous],
  );

  const authConfig = useMemo(() => getAuthConfig(), []);

  /**
   * 인증 Redirect URI
   *
   * 포트 없는 localhost 의 경우 포트 제거한 경로를 제공
   */
  const authRedirectUri = useMemo(() => {
    const redirectUri = authConfig.redirectUri || "";
    const { location } = window || {};

    if (location.host !== "localhost") {
      return redirectUri;
    }

    const uri = new URL(redirectUri);
    return `${uri.protocol}//${uri.hostname}${uri.pathname}${uri.search}`;
  }, [authConfig]);

  // 로그 레벨 설정 - 설정된 레벨 하위 로그 레벨은 표시되지 않음
  useLayoutEffect(() => {
    setLogLevel(logger.logLevel);
  }, [logger]);

  // 쿼리 파라미터로 들어온 로그레벨 설정값이 있으면 설정
  useLayoutEffect(() => {
    if (Object.values(LogLevel).includes(logLevel as LogLevel) && logger?.logLevel !== logLevel) {
      const nextLogger: LoggerState = Object.assign({}, logger, {
        logLevel: logLevel,
      } as LoggerState);

      console.debug("nextLogger", nextLogger);
      setLogger(nextLogger);
    } else if (!logger?.logLevel) {
      setLogger({ logLevel: getDefaultLogLevel() });
    }
  }, [logLevel, logger, setLogger]);

  return (
    <CourtAuthProvider
      issuer={authConfig.jwtIssuer}
      clientId={authConfig.clientId}
      endpoints={{
        authorize: `${authConfig.issuer}/authorize`,
        token: `${authConfig.issuer}/oauth/token`,
        revoke: `${authConfig.issuer}/oauth/revoke`,
      }}
      redirectUri={authRedirectUri}
      scope="openid email phone"
      pending={<></>} // loading 안보이게 처리
      onRedirect={onRedirect}
      onError={onError}
    >
      <HelmetProvider>
        <MetaTag title={getAppTitle()} />
        <Layout />
      </HelmetProvider>
    </CourtAuthProvider>
  );
}

export default Container;
